import { useState } from "react";
import { motion } from "framer-motion";
import { PartyPopper, Trophy, Users, CupSoda, UtensilsCrossed, CalendarDays, CheckCircle2, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { api, formatApiError } from "@/lib/api";
import { CheckoutButton } from "@/components/CheckoutButton";
import { useContent, parseList, parseLines } from "@/lib/content";
import { PixelSprite } from "@/components/PixelSprites";

const HOSPITALITY_ICONS = [CupSoda, UtensilsCrossed, Users];

export default function Events() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Birthday Party", package: "Level 2 Birthday", date: "", notes: "" });
  const [guests, setGuests] = useState(16);
  const [addons, setAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const content = useContent();
  const eh1 = (content["events.h1"] || "").split("|");
  const addonOptions = parseList(content["events.addons"]).filter((r) => r[0]).map((r) => ({ id: r[0], price: parseInt(r[1]) || 0 }));
  const chips = parseLines(content["events.hospitality_chips"]);
  const packages = parseList(content["events.packages"]).filter((r) => r[0]).map((r) => ({ id: r[0], price: r[1], unit: r[2], guests: r[3], features: (r[4] || "").split(";").filter(Boolean), popular: r[5] === "yes" }));
  const tournaments = parseList(content["events.tournaments"]).map((r) => ({ game: r[0], when: r[1], entry: r[2] }));

  const toggleAddon = (a) => setAddons(addons.includes(a) ? addons.filter((x) => x !== a) : [...addons, a]);

  const submit = async () => {
    if (!form.name || !form.email || !form.date) return toast.error("Name, email, and date are required.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions/event-booking", {
        ...form, guests, addons,
      });
      setBooking(data);
      toast.success("Booking request received! Lock in your date with a deposit below.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="events-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={content["images.events_hero"]} alt="Community sports night" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-pink">{content["events.eyebrow"]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl" data-testid="events-heading">
            {eh1[0]} <span className="text-neon-pink glow-magenta">{eh1[1]}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {content["events.intro"]}
          </p>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="packages-section">
        <div className="mb-10 flex items-center gap-3">
          <PixelSprite name="barrel" size={38} className="anim-bob" />
          <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["events.packages_h"]}</h2>
          <PixelSprite name="star" size={30} className="anim-bob-slow" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {packages.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className={`card-hover relative flex flex-col rounded-xl border p-6 ${p.popular ? "border-neon-pink/60 bg-neon-pink/5" : "border-white/10 bg-inkalt"}`}
              data-testid={`package-${p.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {p.popular && (
                <span className="absolute -top-3 left-4 rounded-full bg-neon-pink px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">Most Booked</span>
              )}
              <PartyPopper className={`mb-3 h-6 w-6 ${p.popular ? "text-neon-pink" : "text-slate-400"}`} />
              <h3 className="font-display text-lg font-extrabold uppercase text-white">{p.id}</h3>
              <p className="mt-2"><span className="font-display text-3xl font-black text-white">{p.price}</span>
                <span className="font-mono text-xs text-slate-400"> {p.unit}</span></p>
              <p className="mt-1 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-neon-cyan"><Users className="h-3 w-3" /> {p.guests}</p>
              <ul className="mt-4 flex-1 space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-neon-green" /> {f}
                  </li>
                ))}
              </ul>
              <button onClick={() => { setForm({ ...form, package: p.id }); document.getElementById("booking-form")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`package-select-${p.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`mt-6 w-full rounded-md px-4 py-3 font-display text-xs font-extrabold uppercase tracking-wide transition-colors ${p.popular ? "bg-neon-pink text-white hover:bg-neon-magenta" : "border border-white/20 text-white hover:border-neon-pink hover:text-neon-pink"}`}>
                Book This Package
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="booking-form" className="scroll-mt-24 border-y border-white/10 bg-inkalt/40" data-testid="booking-section">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
          <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-pink">{content["events.booking_eyebrow"]}</p>
          <h2 className="mb-10 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["events.booking_h"]}</h2>

          {booking ? (
            <div className="glass rounded-xl p-10 text-center" data-testid="booking-success">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-neon-green" />
              <h3 className="font-display text-2xl font-extrabold uppercase text-white">Request Received</h3>
              <p className="mt-2 text-sm text-slate-400">Booking reference:</p>
              <p className="mt-2 select-all rounded-md bg-ink px-4 py-2 font-mono text-sm text-neon-pink" data-testid="booking-reference">{booking.id}</p>
              <p className="mx-auto mt-4 max-w-md text-sm text-slate-300">
                {content["events.deposit_text"]}
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <CheckoutButton lookupKey="party_deposit" testId="booking-deposit-checkout-button"
                  className="rounded-md bg-neon-pink px-8 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-magenta">
                  Pay $100 Deposit
                </CheckoutButton>
              </div>
            </div>
          ) : (
            <div className="glass rounded-xl p-6 sm:p-10">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs text-slate-400">Event type</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {["Birthday Party", "Corporate / Team", "Tournament", "Private Venue"].map((t) => (
                      <button key={t} data-testid={`event-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setForm({ ...form, event_type: t })}
                        className={`rounded-full border px-3 py-1.5 font-mono text-xs transition-colors ${form.event_type === t ? "border-neon-pink bg-neon-pink/15 text-neon-pink" : "border-white/15 text-slate-400 hover:border-neon-pink/50"}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Package</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {packages.map((p) => (
                      <button key={p.id} data-testid={`form-package-${p.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setForm({ ...form, package: p.id })}
                        className={`rounded-full border px-3 py-1.5 font-mono text-xs transition-colors ${form.package === p.id ? "border-neon-pink bg-neon-pink/15 text-neon-pink" : "border-white/15 text-slate-400 hover:border-neon-pink/50"}`}>
                        {p.id}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="event-date" className="text-xs text-slate-400">Preferred date *</Label>
                  <Input id="event-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="event-date-input" className="border-white/10 bg-ink text-white" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Guests: <span className="font-mono text-neon-pink" data-testid="guest-count-label">{guests}</span> / 49 max</Label>
                  <Slider value={[guests]} onValueChange={(v) => setGuests(v[0])} min={4} max={49} step={1}
                    data-testid="guest-count-slider" className="mt-3" />
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-xs text-slate-400">Add-ons</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {addonOptions.map((a) => (
                    <button key={a.id} data-testid={`addon-${a.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggleAddon(a.id)}
                      className={`rounded-full border px-3 py-1.5 font-mono text-xs transition-colors ${addons.includes(a.id) ? "border-neon-gold bg-neon-gold/10 text-neon-gold" : "border-white/15 text-slate-400 hover:border-neon-gold/50"}`}>
                      {a.id} · +${a.price}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="event-name-input" placeholder="Name *" className="border-white/10 bg-ink text-white" />
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="event-email-input" placeholder="Email *" className="border-white/10 bg-ink text-white" />
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="event-phone-input" placeholder="Phone" className="border-white/10 bg-ink text-white" />
              </div>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3}
                data-testid="event-notes-input" placeholder="Theme, age group, timing, accessibility needs..."
                className="mt-4 border-white/10 bg-ink text-white" />

              <button onClick={submit} disabled={submitting} data-testid="event-booking-submit-button"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-neon-pink px-6 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-magenta disabled:opacity-60">
                <CalendarDays className="h-4 w-4" /> {submitting ? "Sending..." : "Request Booking"}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* TOURNAMENTS + HOSPITALITY */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2" data-testid="tournaments-section">
        <div>
          <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["events.tournaments_eyebrow"]}</p>
          <h2 className="mb-8 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["events.tournaments_h"]}</h2>
          <div className="space-y-3">
            {tournaments.map((t, i) => (
              <div key={t.game} className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-inkalt p-4"
                data-testid={`tournament-${i}`}>
                <div className="flex items-center gap-3">
                  <Trophy className="h-5 w-5 text-neon-gold" />
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">{t.game}</p>
                    <p className="font-mono text-[11px] text-slate-400">{t.when}</p>
                  </div>
                </div>
                <span className="font-mono text-[11px] text-neon-cyan">{t.entry}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden rounded-xl border border-white/10">
          <img src={content["images.events_crowd"]} alt="Gaming community night" loading="lazy" className="h-full min-h-[300px] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h3 className="font-display text-lg font-extrabold uppercase text-white">{content["events.hospitality_h"]}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {chips.map((c, i) => {
                const Icon = HOSPITALITY_ICONS[i] || Users;
                const iconCls = ["text-neon-pink", "text-neon-gold", "text-neon-cyan"][i] || "text-neon-cyan";
                return (
                  <span key={c} className="flex items-center gap-1.5 rounded-full border border-white/20 bg-ink/70 px-3 py-1.5 font-mono text-[11px] text-slate-200 backdrop-blur">
                    <Icon className={`h-3 w-3 ${iconCls}`} /> {c}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
